import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pill from '../components/ui/Pill';
import { EmptyState, SkeletonRows, ErrorNotice, InfoNotice } from '../components/ui/States';
import { fmt } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';

interface Group {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  protectedReason: string | null;
}

interface GroupMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  archived: boolean;
}

interface AddPreview {
  group: string;
  found: { id: string; name: string; email: string; alreadyMember: boolean; archived: boolean; isActive: boolean }[];
  notFound: string[];
  added: number;
  wouldAdd: number;
}

const inputClass = 'border border-line bg-white px-3 py-[9px] text-table w-full';

function errorMessage(err: unknown, fallback: string) {
  return (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;
}

function GroupForm({
  group,
  onClose,
  onSaved,
}: {
  group: Group | null;
  onClose: () => void;
  onSaved: (g: Group) => void;
}) {
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const save = useMutation(
    async () =>
      group
        ? (await api.patch(`/users/groups/${group.id}`, { name, description })).data.data
        : (await api.post('/users/groups', { name, description })).data.data,
    { onSuccess: (g: Group) => onSaved(g) },
  );
  return (
    <Modal
      title={group ? 'Edit group' : 'New group'}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!name.trim() || save.isLoading} onClick={() => save.mutate()}>
            {save.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <label className="block text-small text-ink-2 mb-1">Name</label>
      <input
        className={inputClass}
        value={name}
        disabled={Boolean(group?.protectedReason)}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. 2027 Fellow"
      />
      {group?.protectedReason ? (
        <div className="text-small text-ink-2 mt-1">This name can’t change: {group.protectedReason}.</div>
      ) : null}
      <label className="block text-small text-ink-2 mt-4 mb-1">Description</label>
      <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} />
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the group.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function AddMembers({ group, initial, onDone }: { group: Group; initial: string; onDone: () => void }) {
  const [text, setText] = useState(initial);
  const [preview, setPreview] = useState<AddPreview | null>(null);
  const run = useMutation(
    async (dryRun: boolean) =>
      (await api.post(`/users/groups/${group.id}/members/by-email`, { emails: text, dryRun })).data.data as AddPreview,
    {
      onSuccess: (data, dryRun) => {
        setPreview(data);
        if (!dryRun) {
          setText('');
          onDone();
        }
      },
    },
  );
  useEffect(() => {
    setText(initial);
    setPreview(null);
  }, [group.id, initial]);

  const toAdd = preview?.found.filter((f) => !f.alreadyMember) ?? [];
  return (
    <div className="border-b border-line pb-5 mb-5">
      <label className="block text-small text-ink-2 mb-1">
        Add members — paste emails, one per line or separated by commas
      </label>
      <textarea
        className={`${inputClass} h-[96px] font-mono text-small`}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setPreview(null);
        }}
        placeholder={'someone@university.ca\nanother@hospital.ca'}
      />
      <div className="flex items-center gap-3 mt-3">
        <Button variant="outline" size="sm" disabled={!text.trim() || run.isLoading} onClick={() => run.mutate(true)}>
          Check emails
        </Button>
        {preview && !preview.added && toAdd.length ? (
          <Button variant="primary" size="sm" disabled={run.isLoading} onClick={() => run.mutate(false)}>
            Add {toAdd.length} to {group.name}
          </Button>
        ) : null}
      </div>
      {run.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(run.error, 'Could not check those emails.')}</ErrorNotice>
        </div>
      ) : null}
      {preview ? (
        <div className="mt-3 text-small">
          {preview.added ? (
            <InfoNotice>
              Added {fmt(preview.added)} member{preview.added === 1 ? '' : 's'} to {preview.group}.
            </InfoNotice>
          ) : (
            <div className="text-ink-2">
              {fmt(toAdd.length)} to add · {fmt(preview.found.length - toAdd.length)} already in the group ·{' '}
              {fmt(preview.notFound.length)} not found
            </div>
          )}
          {toAdd.some((f) => f.archived || !f.isActive) ? (
            <div className="text-ink-2 mt-1">
              Inactive or archived:{' '}
              {toAdd
                .filter((f) => f.archived || !f.isActive)
                .map((f) => f.email)
                .join(', ')}
            </div>
          ) : null}
          {preview.notFound.length ? (
            <div className="mt-1">
              <span className="text-warning">No member with these emails:</span> {preview.notFound.join(', ')}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function UserGroups() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const handedOver = (location.state as { emails?: string[] } | null)?.emails ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [editing, setEditing] = useState<Group | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [removing, setRemoving] = useState<GroupMember | null>(null);
  const [filter, setFilter] = useState('');

  const groups = useQuery<Group[]>('groups', async () => (await api.get('/users/groups')).data.data);
  const list = useMemo(() => groups.data ?? [], [groups.data]);
  const selected = list.find((g) => g.id === selectedId) ?? list[0] ?? null;

  const members = useQuery<GroupMember[]>(
    ['group-members', selected?.id],
    async () => (await api.get(`/users/groups/${selected?.id}/members`)).data.data,
    { enabled: Boolean(selected) },
  );
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = members.data ?? [];
    return q ? rows.filter((m) => `${m.firstName} ${m.lastName} ${m.email}`.toLowerCase().includes(q)) : rows;
  }, [members.data, filter]);

  const refresh = () => {
    queryClient.invalidateQueries('groups');
    queryClient.invalidateQueries('group-members');
  };

  const remove = useMutation(
    async (m: GroupMember) => api.delete(`/users/groups/${selected?.id}/members`, { data: { userIds: [m.id] } }),
    {
      onSuccess: () => {
        setRemoving(null);
        refresh();
      },
    },
  );
  const destroy = useMutation(async () => api.delete(`/users/groups/${selected?.id}`), {
    onSuccess: () => {
      setConfirmDelete(false);
      setSelectedId(null);
      refresh();
    },
  });

  return (
    <div>
      <PageHeader
        title="Groups"
        sub="Who is a Fellow, in which cohort, PI or Co-Investigator, Staff or Partner organization. A member can be in any number of groups."
        aside={
          mutable ? (
            <Button variant="primary" onClick={() => setEditing('new')}>
              New group
            </Button>
          ) : null
        }
      />
      {handedOver.length ? (
        <div className="mb-5">
          <InfoNotice>
            {fmt(handedOver.length)} selected member{handedOver.length === 1 ? '' : 's'} from Members — pick a group,
            then check and add.
          </InfoNotice>
        </div>
      ) : null}

      <div className="md:grid md:grid-cols-[300px_1fr] gap-6 items-start">
        <Panel className={opened ? 'max-md:hidden' : ''}>
          <PanelHeader title="All groups" note={groups.data ? fmt(list.length) : undefined} />
          {groups.isLoading ? (
            <SkeletonRows rows={6} />
          ) : groups.error ? (
            <PanelBody>
              <ErrorNotice>Could not load groups.</ErrorNotice>
            </PanelBody>
          ) : !list.length ? (
            <EmptyState title="No groups yet">Create one with New group.</EmptyState>
          ) : (
            <ul>
              {list.map((g) => (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedId(g.id);
                      setFilter('');
                      setOpened(true);
                    }}
                    className={`w-full flex items-center justify-between px-5 py-[11px] border-b border-line text-left text-table ${
                      selected?.id === g.id ? 'bg-accent-tint' : 'hover:bg-selected'
                    }`}
                  >
                    <span className="truncate">{g.name}</span>
                    <span className="tabular text-ink-2">{fmt(g.memberCount)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {selected ? (
          <Panel className={opened ? '' : 'max-md:hidden'}>
            <button
              type="button"
              onClick={() => setOpened(false)}
              className="md:hidden mb-3 min-h-[44px] text-[15px] text-accent underline underline-offset-2"
            >
              ← All groups
            </button>
            <PanelHeader title={selected.name} note={`${fmt(selected.memberCount)} members`}>
              {mutable ? (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setEditing(selected)}>
                    Edit
                  </Button>
                  {!selected.protectedReason ? (
                    <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)}>
                      Delete
                    </Button>
                  ) : null}
                </>
              ) : null}
            </PanelHeader>
            <PanelBody>
              {selected.description ? <p className="text-table text-ink-2 mb-2">{selected.description}</p> : null}
              {selected.protectedReason ? (
                <p className="text-small text-ink-2 mb-4">Used by the platform: {selected.protectedReason}.</p>
              ) : null}
              {mutable ? <AddMembers group={selected} initial={handedOver.join('\n')} onDone={refresh} /> : null}
              <input
                className={`${inputClass} mb-3`}
                placeholder="Filter by name or email"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {members.isLoading ? (
                <SkeletonRows rows={5} />
              ) : !shown.length ? (
                <EmptyState title={filter ? 'No match' : 'No members yet'}>
                  {filter ? 'Nobody in this group matches the filter.' : 'Paste emails above to add members.'}
                </EmptyState>
              ) : (
                <table className="w-full max-md:block">
                  <tbody className="max-md:block">
                    {shown.map((m) => (
                      <tr
                        key={m.id}
                        className="border-b border-line text-table max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-x-3 max-md:py-2"
                      >
                        <td className="py-[9px] max-md:p-0 max-md:self-center">
                          <button
                            type="button"
                            className="underline text-left"
                            onClick={() => navigate(`/analytics/members/${m.id}`)}
                          >
                            {`${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || m.email}
                          </button>
                          <div className="md:hidden text-small text-ink-2 break-words">{m.email}</div>
                        </td>
                        <td className="py-[9px] text-ink-2 max-md:hidden">{m.email}</td>
                        <td
                          className={`py-[9px] max-md:p-0 max-md:col-span-2 ${m.archived || !m.isActive ? '' : 'max-md:hidden'}`}
                        >
                          {m.archived ? (
                            <Pill tone="inactive">Archived</Pill>
                          ) : !m.isActive ? (
                            <Pill tone="inactive">Inactive</Pill>
                          ) : null}
                        </td>
                        <td className="py-[9px] text-right max-md:p-0 max-md:col-start-2 max-md:row-start-1 max-md:self-center">
                          {mutable ? (
                            <Button variant="ghost" size="sm" onClick={() => setRemoving(m)}>
                              Remove
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </PanelBody>
          </Panel>
        ) : null}
      </div>

      {editing ? (
        <GroupForm
          group={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(g) => {
            setEditing(null);
            setSelectedId(g.id);
            setOpened(true);
            refresh();
          }}
        />
      ) : null}

      {removing && selected ? (
        <Modal
          title="Remove from group"
          onClose={() => setRemoving(null)}
          footer={
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={remove.isLoading} onClick={() => remove.mutate(removing)}>
                Remove
              </Button>
            </div>
          }
        >
          <p className="text-table">
            Remove {`${removing.firstName ?? ''} ${removing.lastName ?? ''}`.trim() || removing.email} from{' '}
            <strong>{selected.name}</strong>? Their member record is not changed.
          </p>
        </Modal>
      ) : null}

      {confirmDelete && selected ? (
        <Modal
          title="Delete group"
          onClose={() => setConfirmDelete(false)}
          footer={
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={destroy.isLoading} onClick={() => destroy.mutate()}>
                Delete group
              </Button>
            </div>
          }
        >
          <p className="text-table">
            Delete <strong>{selected.name}</strong> and its {fmt(selected.memberCount)} memberships? The members
            themselves are kept.
          </p>
          {destroy.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(destroy.error, 'Could not delete the group.')}</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
